<template>
  <app-form-block
    :class="[
      'app-editor',
      {
        'app-editor_disabled': disabled || loadingInput,
        'app-editor_error': errors.length > 0 || errors === true,
      },
    ]"
    :label-style="labelStyle"
    :half="half"
    :errors="Array.isArray(errors) ? errors : []"
    :active="isEditorActive"
  >
    <div
      v-if="loadingInput"
      class="app-select--loading-input"
    >
      <app-dots-in-circle-preloader
          class="mt-button__loading mt-button__loading_only-icon"
      />
    </div>
    
    <template #label>
      {{ label }}
      <slot name="label" />
    </template>

    <div class="editor-container editor-container_classic-editor editor-container_include-style">
      <div class="editor-container__editor">
        <ckeditor
          v-if="isLayoutReady"
          :editor="editor"
          :modelValue="internalValue"
          @update:modelValue="handleEditorUpdate"
          :config="editorConfig"
          @ready="onReady"
          @focus="focusIn"
          @blur="focusOut"
          :disabled="disabled || readonly"
        />
      </div>
      <div class="editor-resize-handle" @mousedown="startResize"></div>
    </div>

    <div v-if="$slots.append" ref="append" class="app-editor__append">
      <slot name="append" />
    </div>
  </app-form-block>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import AppFormBlock from '@/components/UI/AppFormBlock/AppFormBlock.vue';
import AppDotsInCirclePreloader from "@/components/UI/AppDotsInCirclePreloader.vue";
import { Ckeditor } from '@ckeditor/ckeditor5-vue';
import translations from 'ckeditor5/translations/ru.js';
import 'ckeditor5/ckeditor5.css';

import {
  ClassicEditor,
  Alignment,
  Autoformat,
  AutoImage,
  AutoLink,
  Autosave,
  BlockQuote,
  Bold,
  Bookmark,
  Code,
  CodeBlock,
  Essentials,
  FindAndReplace,
  FontBackgroundColor,
  FontColor,
  FontFamily,
  FontSize,
  FullPage,
  GeneralHtmlSupport,
  Heading,
  Highlight,
  HorizontalLine,
  HtmlComment,
  HtmlEmbed,
  ImageBlock,
  ImageCaption,
  ImageInline,
  ImageInsert,
  ImageInsertViaUrl,
  ImageResize,
  ImageStyle,
  ImageTextAlternative,
  ImageToolbar,
  ImageUpload,
  Indent,
  IndentBlock,
  Italic,
  Link,
  LinkImage,
  List,
  ListProperties,
  Markdown,
  MediaEmbed,
  PageBreak,
  Paragraph,
  PasteFromMarkdownExperimental,
  PasteFromOffice,
  RemoveFormat,
  ShowBlocks,
  Base64UploadAdapter,
  SourceEditing,
  SpecialCharacters,
  SpecialCharactersArrows,
  SpecialCharactersCurrency,
  SpecialCharactersEssentials,
  SpecialCharactersLatin,
  SpecialCharactersMathematical,
  SpecialCharactersText,
  Strikethrough,
  Style,
  Subscript,
  Superscript,
  Table,
  TableCaption,
  TableCellProperties,
  TableColumnResize,
  TableProperties,
  TableToolbar,
  TextTransformation,
  TodoList,
  Underline
} from 'ckeditor5';

const LICENSE_KEY = 'GPL';

const props = defineProps({
  disabled: Boolean,
  loadingInput: Boolean,
  errors: {
    type: [Array, Boolean],
    default: () => [],
  },
  half: Boolean,
  label: String,
  placeholder: String,
  modelValue: String,
  small: Boolean,
  height: {
    type: [String, Number],
    default: 300,
  },
  minHeight: {
    type: [String, Number],
    default: 200,
  },
  maxHeight: {
    type: [String, Number],
    default: 800,
  },
  readonly: Boolean,
  labelStyle: {
    type: Object,
    default: () =>{
     return { top: '50px'}
    },
  },
  toolbar:{
    type:Array,
    default: () => [
      'sourceEditing',
      '|',
      'heading',
      'style',
      '|',
      'fontSize',
      'fontFamily',
      'fontColor',
      'fontBackgroundColor',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      '|',
      'link',
      'insertImage',
      'insertTable',
      'codeBlock',
      'htmlEmbed'
    ],
  }
});

const emit = defineEmits(['update:modelValue', 'focus', 'blur', 'ready', 'resize']);

const isEditorActive = ref(false);
const internalValue = ref('');
const editorInstance = ref(null);
const isLayoutReady = ref(false);
const currentHeight = ref(parseInt(props.height));
const isResizing = ref(false);

// Очистка значения от пустых параграфов
const cleanEditorValue = (value) => {
  if (!value || value === '<p>&nbsp;</p>' || value === '<p></p>' || value === '<p><br></p>') {
    return '';
  }
  return value;
};

// Инициализация начального значения
internalValue.value = cleanEditorValue(props.modelValue);
isEditorActive.value = !!internalValue.value;

function handleEditorUpdate(value) {
  const cleanedValue = cleanEditorValue(value);
  internalValue.value = cleanedValue;
  isEditorActive.value = !!cleanedValue;
  emit('update:modelValue', cleanedValue);
}

watch(() => props.modelValue, (newValue) => {
  const cleanedValue = cleanEditorValue(newValue);
  if (cleanedValue !== internalValue.value) {
    internalValue.value = cleanedValue;
    isEditorActive.value = !!cleanedValue;
    if (editorInstance.value && editorInstance.value.getData() !== cleanedValue) {
      editorInstance.value.setData(cleanedValue || '');
    }
  }
});

const editor = ClassicEditor;

const editorConfig = computed(() => {
  if (!isLayoutReady.value) {
    return null;
  }

  return {
    toolbar: {
      items: props.toolbar,
      shouldNotGroupWhenFull: false
    },
    plugins: [
      ImageUpload,
      Base64UploadAdapter,
      Autoformat,
      AutoImage,
      AutoLink,
      Autosave,
      Bold,
      CodeBlock,
      Essentials,
      FontBackgroundColor,
      FontColor,
      FontFamily,
      FontSize,
      FullPage,
      GeneralHtmlSupport,
      Heading,
      HtmlComment,
      HtmlEmbed,
      ImageBlock,
      ImageCaption,
      ImageInline,
      ImageInsertViaUrl,
      ImageResize,
      ImageStyle,
      ImageTextAlternative,
      ImageToolbar,
      Italic,
      Link,
      LinkImage,
      Markdown,
      Paragraph,
      PasteFromMarkdownExperimental,
      PasteFromOffice,
      SourceEditing,
      Strikethrough,
      Style,
      Table,
      TableCaption,
      TableCellProperties,
      TableColumnResize,
      TableProperties,
      TableToolbar,
      TextTransformation,
      Underline
    ],
    image: {
      toolbar: [
        'imageTextAlternative',
        'imageStyle:inline',
        'imageStyle:block',
        'imageStyle:side',
        'linkImage'
      ],
      upload: {
        types: [ 'png', 'jpeg', 'jpg', 'gif', 'webp' ]
      }
    },
    language: 'ru',
    licenseKey: LICENSE_KEY,
    placeholder: props.placeholder || '',
    htmlSupport: {
      allow: [
        {
          name: /.*/,
          attributes: true,
          classes: true,
          styles: true
        }
      ]
    },
  };
});

function onReady(editor) {
  editorInstance.value = editor;
  
  const stickyPanel = editor.ui.view.stickyPanel;
  stickyPanel.view.isSticky = true;
  stickyPanel.view.stickyVerticalOffset = 0;
  
  const updateDropdownPosition = () => {
    nextTick(() => {
      const panels = document.querySelectorAll('.ck-dropdown__panel.ck-dropdown__panel-visible');
      panels.forEach(panel => {
        const rect = panel.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        
        if (rect.bottom > viewportHeight) {
          panel.style.top = 'auto';
          panel.style.bottom = '100%';
        }
      });
    });
  };

  editor.ui.focusTracker.on('change:isFocused', updateDropdownPosition);
  editor.ui.view.toolbar.items.forEach(item => {
    if (item.buttonView) {
      item.buttonView.on('open', updateDropdownPosition);
    }
  });

  const editorElement = editor.ui.getEditableElement();
  const editorContainer = editorElement?.parentElement?.parentElement;
  
  if (editorContainer) {
    editorContainer.style.height = `${currentHeight.value}px`;
    editorContainer.style.minHeight = `${props.minHeight}px`;
    editorContainer.style.maxHeight = `${props.maxHeight}px`;
    editorContainer.style.overflow = 'hidden';
    
    const contentElement = editorElement?.parentElement;
    if (contentElement) {
      const toolbarHeight = editor.ui.view.toolbar.element.offsetHeight;
      contentElement.style.paddingTop = `${toolbarHeight}px`;
      contentElement.style.marginTop = `-${toolbarHeight}px`;
    }
  }
  
  if (editorElement) {
    editorElement.style.height = 'auto';
    editorElement.style.minHeight = '100%';
  }
  
  editor.model.document.on('change:data', () => {
    const newData = editor.getData();
    const cleanedData = cleanEditorValue(newData);
    if (cleanedData !== internalValue.value) {
      internalValue.value = cleanedData;
      isEditorActive.value = !!cleanedData;
      emit('update:modelValue', cleanedData);
    }
  });
  
  emit('ready', editor);
}

function updateEditorHeight(height) {
  const clampedHeight = Math.max(props.minHeight, Math.min(props.maxHeight, height));
  currentHeight.value = clampedHeight;
  
  if (editorInstance.value) {
    const editorElement = editorInstance.value.ui.getEditableElement();
    const editorContainer = editorElement?.parentElement?.parentElement;
    
    if (editorContainer) {
      editorContainer.style.height = `${clampedHeight}px`;
    }
  }
  
  emit('resize', clampedHeight);
}

function startResize(e) {
  isResizing.value = true;
  const startY = e.clientY;
  const startHeight = currentHeight.value;
  
  function doResize(e) {
    const newHeight = startHeight + (e.clientY - startY);
    updateEditorHeight(newHeight);
  }
  
  function stopResize() {
    window.removeEventListener('mousemove', doResize);
    window.removeEventListener('mouseup', stopResize);
    isResizing.value = false;
  }
  
  window.addEventListener('mousemove', doResize);
  window.addEventListener('mouseup', stopResize);
  
  e.preventDefault();
}

function focusIn() {
  isEditorActive.value = true;
  emit('focus');
}

function focusOut() {
  isEditorActive.value = !!internalValue.value;
  emit('blur');
}

onMounted(() => {
  isLayoutReady.value = true;
  isEditorActive.value = !!internalValue.value;
});
</script>

<style lang="scss">
.app-editor {
  &__append {
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
  }

  &.app-editor_disabled {
    opacity: 0.5;
    pointer-events: none;
    
    .ck-editor__editable {
      background: #f5f5f5;
    }
  }

  &.app-editor_loading-input {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 999;
  }

  .editor-container {
    position: relative;
    padding-bottom: 8px;
    
    &_classic-editor {
      border: 1px solid var(--border-color);
      border-radius: 4px;

      .ck-editor__editable.ck-focused {
        border: 0;
      }
    }

    &__editor {
      min-height: v-bind('`${props.minHeight}px`');
      max-height: v-bind('`${props.maxHeight}px`');
      height: v-bind('`${currentHeight}px`');
      resize: none;
      overflow: hidden;
      
      .ck.ck-editor {
        display: flex;
        flex-direction: column;
        height: 100%;
        
        .ck.ck-editor__main {
          flex: 1;
          overflow: auto;
          position: relative;
          
          .ck.ck-sticky-panel {
            position: sticky;
            top: 0;
            z-index: 10;
            background: white;
            
            .ck.ck-toolbar {
              border-top-left-radius: 4px;
              border-top-right-radius: 4px;
            }
          }
          
          .ck.ck-editor__editable {
            min-height: calc(100% - 40px);
            height: auto;
            overflow: hidden;
          }
        }
      }
    }
  }

  .ck.ck-editor__main > .ck-editor__editable:not(.ck-focused) {
    border: 1px solid var(--border-color);
    border-top: 0;
  }

  .ck.ck-dropdown__panel {
    z-index: 10000;
    max-height: 70vh;
    overflow-y: auto;
    
    &.ck-dropdown__panel-visible {
      position: absolute;
      
      &.ck-dropdown__panel_ne {
        bottom: 100%;
        top: auto !important;
      }
    }
  }

  .editor-resize-handle {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 8px;
    cursor: ns-resize;
    background: transparent;
    z-index: 1;

    &:hover {
      background: rgba(0, 0, 0, 0.1);
    }

    &::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 2px;
      width: 20px;
      height: 4px;
      margin-left: -10px;
      background: var(--border-color);
      border-radius: 2px;
    }
  }

  .ck.ck-editor__top .ck-sticky-panel .ck-sticky-panel__content {
    border: 1px solid #b4b4b4;
    border-bottom: 0;
    border-radius: 4px 4px 0 0;
  }

  .ck.ck-editor__main {
    border: 1px solid #b4b4b4;
    border-radius: 0 0 4px 4px;
  }
}
</style>